import { ArrowLeft } from 'lucide-react';
import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { SCAN_SETTING_LIMITS, ScanSettingsSchema, type ScanSettings } from 'shared';
import { toast } from 'sonner';

import { Button } from '../components/ui/Button';
import { Card } from '../components/ui/Card';
import { FormField } from '../components/ui/FormField';
import { useResetScanSettings, useSaveScanSettings } from '../features/marketConsole/mutations';
import { useScanSettings } from '../features/marketConsole/queries';

const MILLION = 1_000_000;
const LIMITS = SCAN_SETTING_LIMITS;

const FIELD_NAMES: Record<string, string> = {
  minAverageDollarVolume30d: 'dollar volume',
  minAdrPct: 'ADR%',
  minAverageVolume10d: '10-day volume',
  topPct: 'top %',
  maxAtrExtension: 'ATR extension',
};

/** Form values in the units people type: $M, shares, %, top-% and ATR multiples. */
interface Draft {
  dollarVolumeM: string;
  minAdrPct: string;
  minAverageVolume10d: string;
  topPct: string;
  maxAtrExtension: string;
  excludeBiotech: boolean;
}

function toDraft(settings: ScanSettings): Draft {
  return {
    dollarVolumeM: String(settings.minAverageDollarVolume30d / MILLION),
    minAdrPct: String(settings.minAdrPct),
    minAverageVolume10d: String(settings.minAverageVolume10d),
    topPct: String(Math.round(settings.topPct * 1000) / 10),
    maxAtrExtension: String(settings.maxAtrExtension),
    excludeBiotech: settings.excludeBiotech,
  };
}

function fromDraft(draft: Draft): ScanSettings | string {
  const parsed = ScanSettingsSchema.safeParse({
    minAverageDollarVolume30d: Number(draft.dollarVolumeM) * MILLION,
    minAdrPct: Number(draft.minAdrPct),
    minAverageVolume10d: Number(draft.minAverageVolume10d),
    topPct: Number(draft.topPct) / 100,
    maxAtrExtension: Number(draft.maxAtrExtension),
    excludeBiotech: draft.excludeBiotech,
  });
  if (parsed.success) return parsed.data;
  const field = parsed.error.issues[0]?.path[0];
  return `Check ${typeof field === 'string' ? (FIELD_NAMES[field] ?? field) : 'the values'}: it is outside the allowed range.`;
}

function errorText(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

export function StockScanSettings() {
  const query = useScanSettings();
  const save = useSaveScanSettings();
  const reset = useResetScanSettings();
  const [draft, setDraft] = useState<Draft | null>(null);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    if (query.data) setDraft(toDraft(query.data.settings));
  }, [query.data]);

  const update = (field: keyof Draft, value: string | boolean) =>
    setDraft((current) => (current ? { ...current, [field]: value } : current));

  const submit = () => {
    if (!draft) return;
    const result = fromDraft(draft);
    if (typeof result === 'string') {
      setError(result);
      return;
    }
    setError(null);
    save.mutate(result, {
      onSuccess: () => toast.success('Scan thresholds saved.'),
      onError: (saveError) => toast.error(errorText(saveError, 'Could not save thresholds.')),
    });
  };

  return (
    <main className="min-h-screen bg-bg px-4 py-6 text-text sm:px-6">
      <div className="mx-auto max-w-3xl space-y-6">
        <header className="space-y-2">
          <Link
            to="/settings"
            className="inline-flex items-center gap-1 text-sm text-text-muted hover:text-text"
          >
            <ArrowLeft className="h-4 w-4" aria-hidden="true" />
            Settings
          </Link>
          <h1 className="text-2xl font-semibold">Stock scan thresholds</h1>
          <p className="text-sm text-text-muted">
            The filters behind Market Console → Stocks, Now and the Flow industry list. They re-rank
            the stored universe, so changes apply at once to every session from the last three
            months. Super liquid leaders keep their own fixed rules.
          </p>
        </header>

        {query.isLoading || !draft ? (
          query.isError ? (
            <p className="text-sm text-danger">Scan thresholds could not be loaded.</p>
          ) : (
            <p className="text-sm text-text-muted">Loading scan thresholds…</p>
          )
        ) : (
          <Card
            header={
              <div className="flex items-baseline justify-between gap-2">
                <h2 className="text-lg font-semibold">Thresholds</h2>
                <span className="text-xs text-text-muted">
                  {query.data?.isDefault ? 'Using the defaults' : 'Custom'}
                </span>
              </div>
            }
          >
            <form
              className="grid gap-4 sm:grid-cols-2"
              onSubmit={(event) => {
                event.preventDefault();
                submit();
              }}
            >
              <FormField
                id="scan-dollar-volume"
                label={`Min 30-day $ volume ($M, ${LIMITS.minAverageDollarVolume30d.min / MILLION}–${LIMITS.minAverageDollarVolume30d.max / MILLION})`}
                type="number"
                inputMode="decimal"
                step="any"
                value={draft.dollarVolumeM}
                onChange={(event) => update('dollarVolumeM', event.target.value)}
              />
              <FormField
                id="scan-adr"
                label={`Min ADR% (${LIMITS.minAdrPct.min}–${LIMITS.minAdrPct.max})`}
                type="number"
                inputMode="decimal"
                step="any"
                value={draft.minAdrPct}
                onChange={(event) => update('minAdrPct', event.target.value)}
              />
              <FormField
                id="scan-volume"
                label={`Min 10-day volume (shares, ${LIMITS.minAverageVolume10d.min.toLocaleString('en-US')}+)`}
                type="number"
                inputMode="numeric"
                step="1"
                value={draft.minAverageVolume10d}
                onChange={(event) => update('minAverageVolume10d', event.target.value)}
              />
              <FormField
                id="scan-top-pct"
                label={`Top % per horizon (${LIMITS.topPct.min * 100}–${LIMITS.topPct.max * 100})`}
                type="number"
                inputMode="decimal"
                step="any"
                value={draft.topPct}
                onChange={(event) => update('topPct', event.target.value)}
              />
              <FormField
                id="scan-extension"
                label={`Max ATRs over the 50-day (${LIMITS.maxAtrExtension.min}–${LIMITS.maxAtrExtension.max})`}
                type="number"
                inputMode="decimal"
                step="any"
                value={draft.maxAtrExtension}
                onChange={(event) => update('maxAtrExtension', event.target.value)}
              />
              <label className="flex items-center gap-2 self-end pb-2 text-sm">
                <input
                  type="checkbox"
                  checked={draft.excludeBiotech}
                  onChange={(event) => update('excludeBiotech', event.target.checked)}
                  className="h-4 w-4"
                />
                Exclude biotech
              </label>
              {error ? (
                <p role="alert" className="text-sm text-danger sm:col-span-2">
                  {error}
                </p>
              ) : null}
              <div className="flex flex-wrap gap-2 sm:col-span-2">
                <Button type="submit" size="sm" disabled={save.isPending}>
                  Save thresholds
                </Button>
                <Button
                  type="button"
                  variant="secondary"
                  size="sm"
                  disabled={reset.isPending || query.data?.isDefault === true}
                  onClick={() =>
                    reset.mutate(undefined, {
                      onSuccess: () => toast.success('Scan thresholds reset to the defaults.'),
                      onError: (resetError) =>
                        toast.error(errorText(resetError, 'Could not reset thresholds.')),
                    })
                  }
                >
                  Reset to defaults
                </Button>
              </div>
            </form>
          </Card>
        )}
      </div>
    </main>
  );
}
